import { useState } from 'react'

import Pagination from '@molecules/Pagination'
import LoadingWrapper from '@molecules/LoadingWrapper'
import { useLocalStorage } from '@hooks/useLocalStorage'
import { useColumnVisibility } from '@hooks/useColumnVisibility'
import type { GroupPageHeaderQueryType, GroupMembersSearchQueryType } from '@frontend/graphql/queries/groups'

import { GroupShowSupporterCard } from '../features/supporters/GroupShowSupporterCard'
import { SupporterDataTable } from '../features/supporters/SupporterDataTable'
import { useSupporterTable } from '../features/supporters/useSupporterTable'
import { SupporterToolbar, type ViewMode } from '../features/supporters/SupporterToolbar'
import { GroupPageHeader } from '../features/group-page/GroupPageHeader'
import { addSupporterUrl } from '../utils/routes'

import SearchSummaryBar from '../features/search-supporters/SearchSummaryBar'
import SupporterSearchEmptyState from '../features/search-supporters/SupporterSearchEmptyState'

import { SupporterFilteringProvider } from '../features/filter-supporters/stores/useSupporterFilteringStore'
import type { BaseActiveFilter } from '../features/filter-supporters/stores/filterStoreSlice'

import { DialogProvider, type DialogRegistry } from '@components/DialogProvider'
import { SendSmsDialog } from '../features/send-sms/SendSmsDialog'
import { LogContactDialog } from '../features/log-contact/LogContactDialog'
import { SendEmailDialog } from '../features/send-email/SendEmailDialog'
import { InitiateCallDialog } from '../features/initiate-call/InitiateCallDialog'
import { SupporterExportDialog } from '../features/export-supporters/SupporterExportDialog'
import { PageLayout } from '@components/layouts/PageLayout'
import type { SharedInertiaProps, RouteProps } from '../types/groupTypes'

const groupSupportersRegistry: DialogRegistry = {
  sendSms: SendSmsDialog,
  logContact: LogContactDialog,
  sendEmail: SendEmailDialog,
  initiateCall: InitiateCallDialog,
  supporterExport: SupporterExportDialog,
}

type GroupSupportersType = {
  group: GroupPageHeaderQueryType['group']
  supporters: GroupMembersSearchQueryType['group']['supporters']
  activeSearch: {
    filters: BaseActiveFilter[]
    page?: string
    sortBy?: string
    supporterName?: string
  }
}

function GroupSupporters({
  group,
  supporters,
  permissions,
  routes,
  activeSearch,
}: GroupSupportersType & SharedInertiaProps & RouteProps) {
  const [searchLoading, setSearchLoading] = useState(false)
  const [viewMode, setViewMode] = useLocalStorage<ViewMode>('viewMode:groupShow', 'card')
  const { columnVisibility, setColumnVisibility } = useColumnVisibility('groupShowSupporters', {
    email: false,
    mobile: false,
    city: false,
    state: false,
    postcode: false,
    tagFilters: false,
    joinedOrganisation: false,
    contactPreferences: false,
    federalDistrict: false,
  })

  const partialReloadKeys = ['supporters', 'activeSearch']
  const { totalCount } = group.supporters
  const matchingSupportersCount = supporters.totalCount
  const currentPage = activeSearch.page ? Number(activeSearch.page) : 1
  const pageSize = supporters.pageSize

  const supporterTable = useSupporterTable({
    data: supporters.edges,
    groupId: group.id,
    columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    canSendIndividualSms: permissions.canSendIndividualSms ?? false,
    canMakePhoneCalls: permissions.canMakePhoneCalls ?? false,
    demoSite: permissions.demoSite ?? false,
  })

  return (
    <SupporterFilteringProvider
      initProps={{ ...activeSearch, setLoading: setSearchLoading, currentSort: activeSearch.sortBy }}
      permissions={permissions}
    >
      <DialogProvider registry={groupSupportersRegistry}>
        <PageLayout.Root>
          <GroupPageHeader group={group} current="supporters" permissions={permissions} routes={routes} />
          <PageLayout.Body>
            <div className="tw:flex tw:flex-col tw:gap-6">
              <div className="tw:w-full">
                <SupporterToolbar
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  table={supporterTable}
                  groupId={group.id}
                  canExportGroupSupporters={
                    Boolean(permissions.canExportGroupSupporters) && supporters.totalCount !== 0
                  }
                  matchingCount={matchingSupportersCount}
                  totalCount={totalCount}
                />

                <LoadingWrapper loading={searchLoading}>
                  <SearchSummaryBar searchLoading={searchLoading} matchingCount={matchingSupportersCount} />

                  {supporters.totalCount === 0 ? (
                    <SupporterSearchEmptyState addSupporterHref={addSupporterUrl(group.id)} totalCount={totalCount} />
                  ) : viewMode === 'table' ? (
                    <SupporterDataTable
                      table={supporterTable}
                      totalCount={matchingSupportersCount}
                      currentPage={currentPage}
                      pageSize={pageSize ?? 20}
                    />
                  ) : (
                    <div role="list" className="tw:flex tw:flex-col tw:gap-4">
                      {supporters.edges.map((supporterEdge, index) => (
                        <GroupShowSupporterCard
                          {...supporterEdge}
                          groupId={group.id}
                          key={supporterEdge.node.id}
                          dataCompId={`supporter-card-${index}`}
                          canSendIndividualSms={permissions.canSendIndividualSms}
                          canMakePhoneCalls={permissions.canMakePhoneCalls}
                          demoSite={permissions.demoSite}
                          electoralFiltersEnabled={permissions.electoralFiltersEnabled}
                          showAllTags={permissions.showAllTags}
                          showBirthdate={permissions.showBirthdate}
                        />
                      ))}
                    </div>
                  )}
                </LoadingWrapper>
              </div>

              <div className="tw:pb-6">
                {matchingSupportersCount > 0 && (
                  <Pagination totalPages={supporters.totalPages ?? 1} only={partialReloadKeys} />
                )}
              </div>
            </div>
          </PageLayout.Body>
        </PageLayout.Root>
      </DialogProvider>
    </SupporterFilteringProvider>
  )
}

export default GroupSupporters
